/**
 * Per-module shading, packed for storage.
 *
 * A table carries one reading per module per sun-position cell: 2,000 modules
 * on the fine grid is over five million numbers, tens of megabytes of JSON in
 * the project. That is past the 10 MB the API accepts, so a large plant's
 * layout could not be saved at all, and every estimate and study request
 * carried it again.
 *
 * What is measured is a COUNT: how many of a module's n sample points the
 * mesh blocks. So each reading is stored as that count, one byte, over the
 * cells the sun can reach (the others are -1 in the table's own values, for
 * every module alike), with runs of zeros -- most of the sky, for most
 * modules -- folded into one byte each, and the bytes base64-encoded. Decoded,
 * a reading is the same three-decimal fraction the table used to store.
 *
 *   byte b < 128   a count of b
 *   byte b >= 128  b - 127 zeros (1..128)
 *
 * The backend reads the same format (pv_core.obstacle_shading); a table that
 * spells its values out is read as it always was.
 */
export const MODULE_SHADING_CODEC = "count-rle-v1";

/** The largest count a byte can hold: a module sampled by more points is stored spelled out. */
export const MAX_PACKED_COUNT = 127;

function toBase64(bytes) {
  let text = "";
  for (let i = 0; i < bytes.length; i += 0x8000) {
    text += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(text);
}

function fromBase64(text) {
  const raw = atob(text);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** Counts (integers 0..127) as the packed string. */
export function packCounts(counts) {
  const out = [];
  let zeros = 0;
  const flush = () => {
    while (zeros > 0) {
      const run = Math.min(128, zeros);
      out.push(127 + run);
      zeros -= run;
    }
  };
  for (const count of counts) {
    if (count === 0) { zeros += 1; continue; }
    if (!(count > 0 && count <= MAX_PACKED_COUNT) || count !== Math.floor(count)) {
      throw new Error(`A packed count must be an integer 0..${MAX_PACKED_COUNT}, not ${count}.`);
    }
    flush();
    out.push(count);
  }
  flush();
  return toBase64(Uint8Array.from(out));
}

/** The counts back, or null when the string does not hold exactly `length` of them. */
export function unpackCounts(packed, length) {
  let bytes;
  try {
    bytes = fromBase64(String(packed));
  } catch {
    return null;
  }
  const counts = new Uint8Array(length);
  let k = 0;
  for (const b of bytes) {
    if (b >= 128) {
      k += b - 127;
    } else {
      if (k >= length) return null;
      counts[k] = b;
      k += 1;
    }
  }
  return k === length ? counts : null;
}

/** The value a count stands for, exactly as the table used to store it. */
const fraction = (count, n) => Math.round((count / n) * 1000) / 1000;

const spelledOut = new WeakMap();

/**
 * The table with every module's values spelled out, whether it was stored
 * packed or not. For the readers of a table; the stored one is left as it is.
 */
export function withModuleValues(table) {
  const modules = table?.moduleShading;
  if (!Array.isArray(modules) || !modules.some((m) => m && !Array.isArray(m.values) && m.packed != null)) {
    return table;
  }
  if (spelledOut.has(table)) return spelledOut.get(table);
  const cells = [];
  (table.values || []).forEach((value, i) => { if (value >= 0) cells.push(i); });
  const moduleShading = modules.map((module) => {
    if (!module || Array.isArray(module.values) || module.packed == null) return module;
    const { packed, n, ...rest } = module;
    const values = new Array((table.values || []).length).fill(-1);
    const counts = unpackCounts(packed, cells.length);
    if (counts && n > 0) cells.forEach((cell, k) => { values[cell] = fraction(counts[k], n); });
    return { ...rest, values };
  });
  const out = { ...table, moduleShading };
  spelledOut.set(table, out);
  return out;
}
